import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, Link, UNSAFE_NavigationContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSmartBack } from "@/lib/navigation-utils";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ArrowLeft,
  ClipboardList,
  FileText,
  Mail,
  Phone,
  ShieldAlert,
  User,
  Printer,
  Sparkles,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, SegmentedTabs } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@akuna/ui";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import {
  MedicalRecordForm,
  type MedicalRecordFormHandle,
} from "@/components/medical-records/MedicalRecordForm";
import { useMedicalRecordForm } from "@/components/medical-records/form/use-medical-record-form";
import { MedicalRecordPatientSummaryCard } from "@/components/medical-records/MedicalRecordPatientSummaryCard";
import { MedicalRecordDocumentsSection } from "@/components/medical-records/MedicalRecordDocumentsSection";
import { AttendanceCarePanel } from "@/components/medical-records/AttendanceCarePanel";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { PrescriptionComposer } from "@/components/prescriptions/PrescriptionComposer";
import { PrescriptionDraftProvider } from "@/components/prescriptions/editor/PrescriptionDraftContext";
import { PrescriptionDraftPreviewCard } from "@/components/prescriptions/editor/PrescriptionDraftPreviewCard";
import { useDraftContextData } from "@/components/prescriptions/editor/useDraftContextData";
import { ApplicationTab } from "@/components/medical-records/ApplicationTab";
import { MedicalRecordExportDialog } from "@/components/medical-records/MedicalRecordExportDialog";
import { PatientTermsPanel } from "@/components/terms/PatientTermsPanel";
import { ProfileDataModal } from "@/components/prescriptions/ProfileDataModal";
import { usePet } from "@/hooks/usePets";
import { useMedicalRecord } from "@/hooks/useMedicalRecord";
import { useMedicalRecords } from "@/hooks/useMedicalRecords";
import { useExamAttachments } from "@/hooks/useExamAttachments";
import { useProntuarioTimeline } from "@/hooks/useProntuarioTimeline";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useIsOwner } from "@/hooks/useIsOwner";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { useProfile } from "@/hooks/useProfile";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { MedicalRecordFormInput, MedicalRecordInput } from "@/lib/validations/medical-record";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import { convertMedicalRecordToFormData } from "@/lib/utils/medical-record";
import { toast } from "sonner";
import { formatDateBR, formatDateTimeBR, parseISODateOrDateTime } from "@/lib/date-utils";
import { createOrUpdateFollowUpAppointment } from "@/lib/utils/follow-up-appointment";
import {
  ReturnApplicationConflictDialog,
  type ReturnApplicationConflictChoice,
  type ApplicationAppointmentSummary,
} from "@/components/medical-records/ReturnApplicationConflictDialog";
import { getLocalDayBounds } from "@/lib/utils/next-dose";
import { getLatestPetWeight } from "@/lib/utils/pet-weight";
import {
  applyPetDeath,
  cancelFutureAppointmentsForPet,
  fetchFutureAppointmentsForPet,
} from "@/lib/utils/pet-death";
import {
  isMedicalRecordInternalTabNavigation,
  resolveNavigationPathname,
} from "@/lib/medical-record-internal-tab-navigation";
import { useTranscription } from "@/contexts/TranscriptionContext";
import { MedicalRecordMobileLayout } from "@/components/medical-records/mobile/MedicalRecordMobileLayout";
import { MobileMedicalRecordHeader } from "@/components/medical-records/mobile/MobileMedicalRecordHeader";
import { MobilePatientChip } from "@/components/medical-records/mobile/MobilePatientChip";
import { MobilePatientEditFlow } from "@/components/medical-records/mobile/MobilePatientEditFlow";
import { MobileMedicalRecordTabs } from "@/components/medical-records/mobile/MobileMedicalRecordTabs";
import { MobileMedicalRecordActionBar } from "@/components/medical-records/mobile/MobileMedicalRecordActionBar";
import { MobilePatientContextSheet } from "@/components/medical-records/mobile/MobilePatientContextSheet";
import { buildMedicalRecordPatientViewModel } from "@/components/medical-records/mobile/medical-record-patient-view-model";
import { resolveMedicalRecordMobileConfig } from "@/components/medical-records/mobile/medical-record-mobile-config";
import type { MedicalRecordPrimaryTab } from "@/components/medical-records/mobile/medical-record-mobile.types";

const formatDate = (value?: string | null) => {
  return formatDateBR(value);
};

type PetConditionEntry = {
  id: string;
  condition?: { id: string; name: string; type: string } | null;
  name?: string | null;
};

type RelatedMedicalRecord = {
  profiles?: {
    id?: string | null;
    full_name?: string | null;
  } | null;
};

const resolvePhotoPath = (
  photo?: { file_path?: string } | { file_path?: string }[] | null,
): string | null => {
  if (!photo) return null;
  if (Array.isArray(photo)) return photo[0]?.file_path ?? null;
  return photo.file_path ?? null;
};

type MedicalRecordRouteTab = "record" | "exams" | "prescriptions" | "applications" | "terms";

const MEDICAL_RECORD_TAB_BY_ROUTE: Record<string, MedicalRecordRouteTab> = {
  record: "record",
  exams: "exams",
  prescriptions: "prescriptions",
  applications: "applications",
  terms: "terms",
  novo: "record",
  exames: "exams",
  prescricao: "prescriptions",
  vacinas: "applications",
  termos: "terms",
};

const ROUTE_TAB_TO_LEGACY: Record<MedicalRecordRouteTab, "novo" | "exames" | "prescricao" | "vacinas" | "termos"> = {
  record: "novo",
  exams: "exames",
  prescriptions: "prescricao",
  applications: "vacinas",
  terms: "termos",
};

const ViewMedicalRecord = () => {
  const { t, i18n } = useTranslation("medicalRecords");
  const navigate = useNavigate();
  const location = useLocation();
  const smartBack = useSmartBack();
  const { id, recordTab } = useParams<{ id: string; recordTab?: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const routeTab = useMemo<MedicalRecordRouteTab | null>(() => {
    if (!recordTab) return null;
    return MEDICAL_RECORD_TAB_BY_ROUTE[recordTab] ?? null;
  }, [recordTab]);
  const activeTab: "novo" | "exames" | "prescricao" | "vacinas" | "termos" = ROUTE_TAB_TO_LEGACY[routeTab ?? "record"];
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [isMobileContextOpen, setIsMobileContextOpen] = useState(false);
  const [isMobileEditOpen, setIsMobileEditOpen] = useState(false);
  const [showProfileDataModal, setShowProfileDataModal] = useState(false);
  const [pendingMedicalRecordUpdate, setPendingMedicalRecordUpdate] = useState<MedicalRecordFormInput | null>(null);
  const [returnVaccineConflictOpen, setReturnVaccineConflictOpen] = useState(false);
  const [returnVaccineConflictChoice, setReturnVaccineConflictChoice] = useState<ReturnApplicationConflictChoice | null>(null);
  const [returnVaccineConflictContext, setReturnVaccineConflictContext] = useState<{
    followUpDate: Date;
    vaccineAppointments: ApplicationAppointmentSummary[];
  } | null>(null);
  const [existingFollowUpAppointment, setExistingFollowUpAppointment] = useState<ApplicationAppointmentSummary | null>(null);
  const returnVaccineConflictResolverRef = useRef<((value: ReturnApplicationConflictChoice | null) => void) | null>(null);
  const formRef = useRef<MedicalRecordFormHandle>(null);
  const pendingNavigationActionRef = useRef<(() => void) | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);
  const isProcessingBlockRef = useRef(false);
  const skipUnsavedCheckRef = useRef(false);
  const locationPathnameRef = useRef(location.pathname);
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  const promptReturnVaccineConflictResolution = useCallback(
    (ctx: { followUpDate: Date; vaccineAppointments: ApplicationAppointmentSummary[] }) =>
      new Promise<ReturnApplicationConflictChoice | null>((resolve) => {
        returnVaccineConflictResolverRef.current = resolve;
        setReturnVaccineConflictContext(ctx);
        setReturnVaccineConflictChoice({ keepFollowUp: true, keepVaccines: true });
        setReturnVaccineConflictOpen(true);
      }),
    [],
  );

  const resolveReturnVaccineConflict = useCallback((value: ReturnApplicationConflictChoice | null) => {
    const resolver = returnVaccineConflictResolverRef.current;
    returnVaccineConflictResolverRef.current = null;
    setReturnVaccineConflictOpen(false);
    resolver?.(value);
  }, []);

  const queryClient = useQueryClient();
  const {
    data: medicalRecord,
    isLoading: isLoadingRecord,
    error: recordError,
  } = useMedicalRecord(id);

  const petId = medicalRecord?.pet_id;
  const { data: pet, isLoading: isLoadingPet, error: petError } = usePet(petId ?? "");
  const { medicalRecords, updateMedicalRecord } = useMedicalRecords(petId || undefined);
  const { data: prontuarioTimeline } = useProntuarioTimeline(id);
  const { data: profile, isLoading: isLoadingProfile } = useProfile();
  const draftContext = useDraftContextData({ pet, medicalRecordId: medicalRecord?.id ?? null });
  const { user: currentUser } = useAuth();
  const { open: openTranscription } = useTranscription();

  // Check permissions and authorization
  const { hasPermission: hasUpdatePermission, isLoading: isLoadingPermission } = useHasPermissionSync("medical_records", "edit");
  const { data: isOwner, isLoading: isLoadingOwner } = useIsOwner();
  const { data: isSuperAdmin, isLoading: isLoadingSuperAdmin } = useIsSuperAdmin();

  // Calculate if user can edit
  const canEdit = useMemo(() => {
    if (isLoadingPermission || isLoadingOwner || isLoadingSuperAdmin || !medicalRecord || !currentUser) {
      return false; // Wait for all checks
    }

    // Must have update permission
    if (!hasUpdatePermission) {
      return false;
    }

    // Super Admin and Owner can edit any record
    if (isSuperAdmin || isOwner) {
      return true;
    }

    // Regular users can only edit their own records
    return medicalRecord.vet_user_id === currentUser.id;
  }, [hasUpdatePermission, isOwner, isSuperAdmin, medicalRecord, currentUser, isLoadingPermission, isLoadingOwner, isLoadingSuperAdmin]);

  // Determine the reason why editing is blocked (for error messages)
  const editBlockReason = useMemo(() => {
    if (isLoadingPermission || isLoadingOwner || isLoadingSuperAdmin || !medicalRecord || !currentUser) {
      return null; // Still loading
    }

    if (!hasUpdatePermission) {
      return "no_permission";
    }

    if (isSuperAdmin || isOwner) {
      return null; // Can edit
    }

    if (medicalRecord.vet_user_id !== currentUser.id) {
      return "not_creator";
    }

    return null; // Can edit
  }, [hasUpdatePermission, isOwner, isSuperAdmin, medicalRecord, currentUser, isLoadingPermission, isLoadingOwner, isLoadingSuperAdmin]);

  const isEditAuthorizationResolved =
    !isLoadingPermission &&
    !isLoadingOwner &&
    !isLoadingSuperAdmin &&
    Boolean(medicalRecord) &&
    Boolean(currentUser);

  useEffect(() => {
    if (routeTab) return;
    if (!id) return;
    navigate(`/medical-records/${id}/edit/record`, { replace: true });
  }, [id, navigate, routeTab]);

  useEffect(() => {
    if (!id || !isEditAuthorizationResolved || canEdit || !editBlockReason) return;

    const message =
      editBlockReason === "not_creator"
        ? t("editOnlyResponsibleVet")
        : t("noEditPermission");

    toast.error(message);
    navigate(`/medical-records/${id}`, { replace: true });
  }, [id, isEditAuthorizationResolved, canEdit, editBlockReason, navigate, t]);

  const clinicId = medicalRecord?.clinic_id || profile?.current_clinic_id || "";
  const normalizedClinicId = clinicId || null;
  const hasCRMV = Boolean(
    profile?.crmv_state &&
    profile.crmv_state.trim() !== "" &&
    profile?.crmv_number &&
    profile.crmv_number > 0
  );

  const {
    attachments: examAttachments,
    isLoading: isLoadingAttachments,
    uploadAttachments,
    deleteAttachment,
    updateAttachment,
  } = useExamAttachments(pet?.id, undefined, normalizedClinicId);

  const age = useMemo(() => {
    if (!pet?.birth_date) return null;
    const birthDate = parseISODateOrDateTime(pet.birth_date);
    if (!birthDate) return null;
    return formatDistanceToNow(birthDate, {
      addSuffix: false,
      locale: ptBR,
    });
  }, [pet?.birth_date]);

  const lastWeight = useMemo(() => {
    return getLatestPetWeight((pet?.weights ?? []) as Array<{ weight: number; measured_at?: string | null; created_at?: string | null; deleted_at?: string | null }>);
  }, [pet?.weights]);

  const lastMedicalRecord = useMemo(() => {
    if (!medicalRecords?.length) return null;
    // Excluir o registro atual da lista
    return medicalRecords.find((r) => r.id !== id) ?? medicalRecords[0];
  }, [medicalRecords, id]);
  const lastMedicalRecordVetName = (lastMedicalRecord as (typeof lastMedicalRecord & RelatedMedicalRecord) | null)?.profiles?.full_name?.trim() || null;

  useEffect(() => {
    let cancelled = false;

    const loadExistingFollowUpAppointment = async () => {
      if (!medicalRecord?.follow_up_date || !clinicId || !pet?.owner?.id || !pet?.id) {
        setExistingFollowUpAppointment(null);
        return;
      }

      const { data: retornoType } = await supabase
        .from("clinic_appointment_types")
        .select("id")
        .eq("clinic_id", clinicId)
        .eq("behavior", "retorno")
        .maybeSingle();

      if (!retornoType) {
        if (!cancelled) setExistingFollowUpAppointment(null);
        return;
      }

      const previousFollowUpDate = new Date(medicalRecord.follow_up_date);
      const timeWindowStart = new Date(previousFollowUpDate.getTime() - 5 * 60 * 1000);
      const timeWindowEnd = new Date(previousFollowUpDate.getTime() + 5 * 60 * 1000);

      const { data: existingAppointments, error: existingAppointmentsError } = await supabase
        .from("appointments")
        .select("id, starts_at")
        .eq("clinic_id", clinicId)
        .eq("owner_id", pet.owner.id)
        .eq("type", retornoType.id)
        .gte("starts_at", timeWindowStart.toISOString())
        .lte("starts_at", timeWindowEnd.toISOString())
        .in("status", ["agendado", "confirmado"])
        .is("deleted_at", null)
        .limit(10);

      if (existingAppointmentsError) {
        console.warn("Não foi possível buscar agendamento(s) de retorno existente(s):", existingAppointmentsError);
        if (!cancelled) setExistingFollowUpAppointment(null);
        return;
      }

      const appointments = (existingAppointments ?? []) as ApplicationAppointmentSummary[];
      if (appointments.length === 0) {
        if (!cancelled) setExistingFollowUpAppointment(null);
        return;
      }

      const candidateIds = appointments.map((appt) => appt.id);
      const { data: appointmentPets, error: appointmentPetsError } = await supabase
        .from("appointment_pets")
        .select("appointment_id, pet_id")
        .in("appointment_id", candidateIds);

      if (appointmentPetsError) {
        console.warn("Não foi possível verificar vínculo do paciente no retorno existente:", appointmentPetsError);
        if (!cancelled) setExistingFollowUpAppointment(null);
        return;
      }

      const matchedId = (appointmentPets ?? []).find((ap) => ap.pet_id === pet.id)?.appointment_id ?? null;
      const matchedAppointment = appointments.find((appt) => appt.id === matchedId) ?? null;

      if (!cancelled) {
        setExistingFollowUpAppointment(matchedAppointment);
      }
    };

    void loadExistingFollowUpAppointment();

    return () => {
      cancelled = true;
    };
  }, [clinicId, medicalRecord?.follow_up_date, pet?.id, pet?.owner?.id]);

  const formDefaultValues = useMemo(() => {
    if (!medicalRecord || !pet) return undefined;
    const converted = convertMedicalRecordToFormData(medicalRecord, pet) as Partial<MedicalRecordFormInput>;
    const startsAt = existingFollowUpAppointment?.starts_at ?? null;

    return {
      ...converted,
      create_follow_up_appointment: Boolean(existingFollowUpAppointment),
      follow_up_time: startsAt ? format(new Date(startsAt), "HH:mm") : "",
    };
  }, [existingFollowUpAppointment, medicalRecord, pet]);

  const formController = useMedicalRecordForm({
    petId: pet?.id ?? petId ?? "",
    appointmentId: medicalRecord?.appointment_id || undefined,
    clinicId,
    defaultValues: formDefaultValues,
    onSubmit: async (data) => handleSubmit(data),
    onDirtyChange: setHasFormChanges,
  });

  // Check if there's unsaved data
  const hasUnsavedData = useCallback(async (): Promise<boolean> => {
    if (skipUnsavedCheckRef.current) {
      skipUnsavedCheckRef.current = false;
      return false;
    }

    const isFormDirty = formRef.current?.isDirty?.() ?? false;
    return isFormDirty || hasFormChanges;
  }, [hasFormChanges]);

  // Handle navigation with confirmation
  const handleNavigation = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const navigationContext = useContext(UNSAFE_NavigationContext);
  const navigatorInstance = navigationContext?.navigator;

  const attemptNavigation = useCallback(
    async (execute: () => void) => {
      if (isProcessingBlockRef.current) {
        return;
      }

      isProcessingBlockRef.current = true;
      try {
        const hasData = await hasUnsavedData();
        if (hasData) {
          pendingNavigationActionRef.current = execute;
          setIsExitDialogOpen(true);
        } else {
          execute();
        }
      } finally {
        isProcessingBlockRef.current = false;
      }
    },
    [hasUnsavedData],
  );

  useEffect(() => {
    if (!navigatorInstance) return;

    const nav = navigatorInstance as unknown as {
      push?: (...args: unknown[]) => void;
      replace?: (...args: unknown[]) => void;
      go?: (...args: unknown[]) => void;
    };

    const cleanupCallbacks: Array<() => void> = [];

    if (typeof nav.push === "function") {
      const originalPush = nav.push;
      nav.push = ((...args: unknown[]) => {
        const nextPath = resolveNavigationPathname(args[0], locationPathnameRef.current);
        if (nextPath && isMedicalRecordInternalTabNavigation(locationPathnameRef.current, nextPath)) {
          originalPush.apply(navigatorInstance, args as unknown[]);
          return;
        }
        void attemptNavigation(() => {
          originalPush.apply(navigatorInstance, args as unknown[]);
        });
      }) as typeof nav.push;
      cleanupCallbacks.push(() => {
        nav.push = originalPush;
      });
    }

    if (typeof nav.replace === "function") {
      const originalReplace = nav.replace;
      nav.replace = ((...args: unknown[]) => {
        const nextPath = resolveNavigationPathname(args[0], locationPathnameRef.current);
        if (nextPath && isMedicalRecordInternalTabNavigation(locationPathnameRef.current, nextPath)) {
          originalReplace.apply(navigatorInstance, args as unknown[]);
          return;
        }
        void attemptNavigation(() => {
          originalReplace.apply(navigatorInstance, args as unknown[]);
        });
      }) as typeof nav.replace;
      cleanupCallbacks.push(() => {
        nav.replace = originalReplace;
      });
    }

    if (typeof nav.go === "function") {
      const originalGo = nav.go;
      nav.go = ((...args: unknown[]) => {
        void attemptNavigation(() => {
          originalGo.apply(navigatorInstance, args as unknown[]);
        });
      }) as typeof nav.go;
      cleanupCallbacks.push(() => {
        nav.go = originalGo;
      });
    }

    if (cleanupCallbacks.length === 0) {
      return;
    }

    unblockRef.current = () => {
      cleanupCallbacks.forEach((cb) => cb());
      unblockRef.current = null;
    };

    return () => {
      unblockRef.current?.();
    };
  }, [attemptNavigation, navigatorInstance]);

  const handleConfirmExit = useCallback(async () => {
    setIsExitDialogOpen(false);
    const action = pendingNavigationActionRef.current;
    pendingNavigationActionRef.current = null;
    setHasFormChanges(false);
    skipUnsavedCheckRef.current = false;

    if (action) {
      action();
    }
  }, []);

  const handleCancelExit = useCallback(() => {
    pendingNavigationActionRef.current = null;
    setIsExitDialogOpen(false);
  }, []);

  // Intercept back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isFormDirty = formRef.current?.isDirty?.() ?? false;
      if (isFormDirty || hasFormChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasFormChanges]);

  if (!id) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">
            {t("notFound")}
          </h1>
        </div>
      </div>
    );
  }

  if (isLoadingRecord || isLoadingPet || isLoadingProfile) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-56" />
        </div>
        <Skeleton className="h-20 w-full" />
        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <Skeleton className="h-[640px] w-full" />
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (recordError || !medicalRecord || petError || !pet) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">
            {t("loadErrorTitle")}
          </h1>
        </div>
        <Card>
          <CardContent className="py-6">
            <p className="text-muted-foreground">
              {t("loadErrorDescription")}
            </p>
            <Button className="mt-4" onClick={() => smartBack()}>
              {t("backToPatients")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleBack = () =>
    smartBack(`/patients/pets/${pet.id}/history`);

  const ownerPrimaryPhone =
    pet?.owner?.phones?.find((phone) => phone.is_primary) ?? pet?.owner?.phones?.[0] ?? null;
  const ownerPrimaryPhoneLabel = ownerPrimaryPhone?.phone_number?.trim()
    ? ownerPrimaryPhone.phone_number
    : null;
  const ownerPrimaryEmail =
    pet?.owner?.emails?.find((email) => email.is_primary)?.email ??
    pet?.owner?.emails?.[0]?.email;
  const responsibleName = medicalRecord?.pets?.owners?.name?.trim() || pet?.owner?.name?.trim() || null;
  const veterinarianName = medicalRecord?.profiles?.full_name?.trim() || null;

  const handleUpdateMedicalRecord = async (data: MedicalRecordFormInput) => {
    if (!id) return;

    try {
      setIsSubmitting(true);
      const resolvedAppointmentId = data.appointment_id?.trim() || null;

      // Update medical record
      const updatedRecord = await updateMedicalRecord({
        id,
        data: {
          appointment_id: resolvedAppointmentId,
          consultation_date: data.consultation_date.toISOString(),
          chief_complaint: data.chief_complaint || null,
          history: data.history || null,
          behavior: data.behavior || null,
          diet: data.diet || null,
          environment: data.environment || null,
          weight: data.weight || null,
          temperature: data.temperature || null,
          heart_rate: data.heart_rate || null,
          respiratory_rate: data.respiratory_rate || null,
          mucous_membranes: data.mucous_membranes || null,
          capillary_refill_time: data.capillary_refill_time || null,
          hydration_status: data.hydration_status || null,
          lymph_nodes: data.lymph_nodes || null,
          cardiac_auscultation: data.cardiac_auscultation || null,
          respiratory_auscultation: data.respiratory_auscultation || null,
          abdominal_palpation: data.abdominal_palpation || null,
          locomotor_system: data.locomotor_system || null,
          skin_coat: data.skin_coat || null,
          other_systems: data.other_systems || null,
          presumptive_diagnosis: data.presumptive_diagnosis || null,
          definitive_diagnosis: data.definitive_diagnosis || null,
          treatment: data.treatment || null,
          follow_up_date: data.follow_up_date
            ? data.follow_up_date.toISOString()
            : null,
          follow_up_notes: data.follow_up_notes || null,
        },
      });

      if (data.register_death && data.death_date) {
        await applyPetDeath({
          petId: pet.id,
          deathDate: data.death_date,
          deathCause: data.death_cause,
          ownerId: pet.owner?.id,
          queryClient,
        });

        const futureAppointments = await fetchFutureAppointmentsForPet(pet.id);
        if (futureAppointments.length > 0) {
          const shouldCancel = window.confirm(
            t("death.confirmFutureAppointments", { count: futureAppointments.length }),
          );
          if (shouldCancel) {
            await cancelFutureAppointmentsForPet(pet.id);
            queryClient.invalidateQueries({ queryKey: ["appointments"] });
          }
        }
      }

      // Aguardar refetch das queries antes de navegar para garantir dados atualizados
      await queryClient.refetchQueries({
        queryKey: ["medical-record", id]
      });
      await queryClient.invalidateQueries({ queryKey: ["medical-prescriptions"] });
      await queryClient.invalidateQueries({ queryKey: ["pet-attachments"] });

      const wantsFollowUpAppointment = Boolean(data.follow_up_date && data.create_follow_up_appointment);

      // Criar, atualizar ou cancelar agendamento de retorno apenas quando solicitado explicitamente
      if (wantsFollowUpAppointment || existingFollowUpAppointment) {
        if (!clinicId) {
          if (wantsFollowUpAppointment) {
            console.warn("Não foi possível agendar retorno: clínica não definida.");
          }
        } else if (!pet?.owner?.id) {
          if (wantsFollowUpAppointment) {
            console.warn("Não foi possível agendar retorno: responsável sem identificador.");
          }
        } else {
          try {
            let shouldScheduleReturn = wantsFollowUpAppointment;
            let followUpDateForSchedule = data.follow_up_date ?? null;

            if (wantsFollowUpAppointment && data.follow_up_date) {
              const { start, end } = getLocalDayBounds(data.follow_up_date);
            const { data: vaccineAppointmentsRaw, error: vaccineAppointmentsError } = await supabase
              .from("appointments")
              .select("id, starts_at")
              .eq("clinic_id", clinicId)
              .eq("type", "vacina")
              .gte("starts_at", start.toISOString())
              .lte("starts_at", end.toISOString())
              .in("status", ["agendado", "confirmado"])
              .is("deleted_at", null)
              .limit(20);

            if (vaccineAppointmentsError) {
              console.warn("Não foi possível verificar conflito de agendamentos de vacina:", vaccineAppointmentsError);
            }

            const vaccineAppointmentsCandidates = (vaccineAppointmentsRaw ?? []) as ApplicationAppointmentSummary[];
            let vaccineAppointmentsForPet: ApplicationAppointmentSummary[] = [];

            if (vaccineAppointmentsCandidates.length > 0) {
              const ids = vaccineAppointmentsCandidates.map((a) => a.id);
              const { data: relations, error: relationsError } = await supabase
                .from("appointment_pets")
                .select("appointment_id")
                .eq("pet_id", pet.id)
                .in("appointment_id", ids);

              if (relationsError) {
                console.warn("Não foi possível verificar vínculo do paciente nos agendamentos de vacina:", relationsError);
              } else {
                const allowed = new Set((relations ?? []).map((r) => r.appointment_id));
                vaccineAppointmentsForPet = vaccineAppointmentsCandidates.filter((a) => allowed.has(a.id));
              }
            }

              let conflictChoice: ReturnApplicationConflictChoice | null = null;
              if (vaccineAppointmentsForPet.length > 0) {
                conflictChoice = await promptReturnVaccineConflictResolution({
                  followUpDate: data.follow_up_date,
                  vaccineAppointments: vaccineAppointmentsForPet,
                });

                if (conflictChoice && !conflictChoice.keepVaccines) {
                  const ids = vaccineAppointmentsForPet.map((a) => a.id);
                  let hadDeleteError = false;
                  for (const appointmentId of ids) {
                    const { error } = await supabase.rpc("soft_delete_appointment", {
                      p_appointment_id: appointmentId,
                    });
                    if (error) {
                      hadDeleteError = true;
                      console.warn("Não foi possível remover agendamento(s) de vacina:", error);
                    }
                  }

                  const { error: unlinkError } = await supabase
                    .from("pet_applications")
                    .update({ appointment_id: null })
                    .eq("pet_id", pet.id)
                    .in("appointment_id", ids);
                  if (unlinkError) {
                    console.warn("Não foi possível desvincular agendamento(s) das vacinações:", unlinkError);
                  }

                  if (!hadDeleteError) {
                    queryClient.invalidateQueries({ queryKey: ["appointments"] });
                    invalidateReportsCache(queryClient);
                  }
                }

                if (conflictChoice && !conflictChoice.keepFollowUp) {
                  const { error: clearError } = await supabase
                    .from("medical_records")
                    .update({ follow_up_date: null, follow_up_notes: null })
                    .eq("id", id);

                  if (clearError) {
                    console.warn("Não foi possível remover data de retorno do prontuário:", clearError);
                  }

                  shouldScheduleReturn = false;
                  followUpDateForSchedule = null;
                }
              }
            }

            if (!shouldScheduleReturn && existingFollowUpAppointment?.id) {
              const { error } = await supabase.rpc("soft_delete_appointment", {
                p_appointment_id: existingFollowUpAppointment.id,
              });
              if (error) {
                console.warn("Não foi possível remover agendamento de retorno:", error);
              } else {
                queryClient.invalidateQueries({ queryKey: ["appointments"] });
                invalidateReportsCache(queryClient);
              }
            }

            const appointmentId = shouldScheduleReturn && followUpDateForSchedule
              ? await createOrUpdateFollowUpAppointment({
                medicalRecordId: id,
                petId: pet.id,
                ownerId: pet.owner.id,
                clinicId: clinicId,
                vetUserId: updatedRecord?.vet_user_id ?? medicalRecord?.vet_user_id ?? null,
                followUpDate: followUpDateForSchedule,
                followUpNotes: data.follow_up_notes || null,
                existingAppointmentId: existingFollowUpAppointment?.id ?? null,
              })
              : null;

            if (appointmentId) {
              toast.success(t("update.followUpScheduled"));
              // Invalidar queries para atualizar a lista de agendamentos
              queryClient.invalidateQueries({ queryKey: ["appointments"] });
              invalidateReportsCache(queryClient);
            }
          } catch (appointmentError) {
            console.error("Erro ao agendar retorno:", appointmentError);
            toast.error(t("update.followUpScheduleError"));
          }
        }
      }

      formRef.current?.reset(data);
      setHasFormChanges(false);
      skipUnsavedCheckRef.current = true;
      navigate(`/medical-records/${id}/record`);
    } catch (submitError) {
      console.error("Erro ao atualizar consulta:", submitError);
      const message =
        submitError instanceof Error
          ? submitError.message
          : t("update.updateError");
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (data: MedicalRecordFormInput) => {
    if (!hasCRMV) {
      setPendingMedicalRecordUpdate(data);
      setShowProfileDataModal(true);
      return;
    }

    await handleUpdateMedicalRecord(data);
  };

  const handleProfileDataComplete = async () => {
    setShowProfileDataModal(false);
    await queryClient.refetchQueries({ queryKey: ["profile"] });
    const updatedProfile = queryClient.getQueryData<{
      crmv_state?: string | null;
      crmv_number?: number | null;
    }>(["profile"]);
    const hasCrmv = Boolean(
      updatedProfile?.crmv_state &&
      updatedProfile.crmv_state.trim() !== "" &&
      updatedProfile?.crmv_number &&
      updatedProfile.crmv_number > 0
    );
    const pendingRecord = pendingMedicalRecordUpdate;

    if (pendingRecord && hasCrmv) {
      setPendingMedicalRecordUpdate(null);
      await handleUpdateMedicalRecord(pendingRecord);
    } else {
      setPendingMedicalRecordUpdate(null);
    }
  };

  const petPhoto = resolvePhotoPath(pet.photo ?? null);
  const petSubtitle = [pet.species?.name, pet.breed?.name, age].filter(Boolean).join(" • ");
  const petAllergies = (pet.allergies ?? []) as PetConditionEntry[];
  const petTemperaments = (pet.temperaments ?? []) as PetConditionEntry[];
  const mobileActiveTab: MedicalRecordPrimaryTab = {
    novo: "record",
    exames: "documents",
    prescricao: "prescriptions",
    vacinas: "applications",
    termos: "terms",
  }[activeTab] as MedicalRecordPrimaryTab;
  const mobileConfig = resolveMedicalRecordMobileConfig({ mode: "edit", canEdit });
  const mobilePatient = buildMedicalRecordPatientViewModel({
    pet,
    age,
    lastWeight,
    locale: i18n.resolvedLanguage ?? i18n.language,
  });
  const mobileTabs = [
    { value: "record" as const, label: t("tabs.record") },
    { value: "documents" as const, label: t("tabs.exams") },
    { value: "prescriptions" as const, label: t("tabs.prescriptions") },
    { value: "applications" as const, label: t("tabs.applications") },
    { value: "terms" as const, label: t("tabs.terms") },
  ];
  const mobileRouteByTab: Record<MedicalRecordPrimaryTab, string> = {
    record: "record",
    documents: "exams",
    prescriptions: "prescriptions",
    applications: "applications",
    terms: "terms",
  };
  const openMobileTranscription = () =>
    openTranscription({
      petId: pet.id,
      clinicId,
      appointmentId: medicalRecord.appointment_id,
      medicalRecordId: medicalRecord.id,
      petName: pet.name,
      appointmentDate: medicalRecord.consultation_date,
      onApply: (values) => formRef.current?.applyValues(values),
      onMergeFields: (values) => formRef.current?.applyValues(values),
    });

  return (
    <PrescriptionDraftProvider context={draftContext}>
      <MedicalRecordMobileLayout shouldShowTranscriptionBar={false}
        header={
          <MobileMedicalRecordHeader
            title={t("mobile.titles.edit")}
            subtitle={[pet.name, formatDateTimeBR(medicalRecord.consultation_date)].filter(Boolean).join(" · ")}
            backLabel={t("mobile.actions.back")}
            contextLabel={t("mobile.actions.openContext")}
            transcriptionLabel={t("mobile.actions.openTranscription")}
            moreLabel={t("mobile.actions.more")}
            showContext={mobileConfig.showContext}
            showTranscription={mobileConfig.showTranscription}
            showOverflow={mobileConfig.showOverflow}
            onBack={handleBack}
            onOpenContext={() => setIsMobileContextOpen(true)}
            onOpenTranscription={openMobileTranscription}
          />
        }
        patient={<MobilePatientChip viewModel={mobilePatient} phoneLabel={t("mobile.actions.callGuardian")} editLabel={t("quickEdit.editPetAriaLabel")} onEdit={() => setIsMobileEditOpen(true)} />}
        tabs={
          <MobileMedicalRecordTabs
            activeTab={mobileActiveTab}
            tabs={mobileTabs}
            onTabChange={(tab) =>
              navigate(`/medical-records/${id}/edit/${mobileRouteByTab[tab]}`, { replace: true })
            }
          />
        }
        actions={
          <MobileMedicalRecordActionBar
            config={mobileConfig}
            secondaryLabel={t("actions.cancel")}
            primaryLabel={isSubmitting ? t("actions.saving") : t("mobile.actions.saveChanges")}
            isSubmitting={isSubmitting}
            onSecondary={handleBack}
            onPrimary={() => formRef.current?.submit()}
          />
        }
        overlays={
          <MobilePatientContextSheet
            open={isMobileContextOpen}
            onOpenChange={setIsMobileContextOpen}
            title={t("mobile.context.title")}
            description={t("mobile.context.description")}
            closeLabel={t("mobile.context.close")}
            records={medicalRecords ?? []}
            isLoadingRecords={false}
            petId={pet.id}
            ownerId={pet.owner?.id ?? null}
            clinicId={clinicId}
          />
        }
      >
        {isMobile && activeTab === "novo" && clinicId && canEdit ? (
          <MedicalRecordForm
            ref={formRef}
            formController={formController}
            onCancel={handleBack}
            isLoading={isSubmitting}
            variant="mobile"
          />
        ) : null}
        {activeTab === "exames" ? (
          <MedicalRecordDocumentsSection
            variant="mobile"
            clinicId={clinicId}
            attachments={examAttachments}
            isLoadingAttachments={isLoadingAttachments}
            onUploadAttachments={uploadAttachments}
            onDeleteAttachment={deleteAttachment}
            onUpdateAttachment={updateAttachment}
          />
        ) : null}
        {activeTab === "prescricao" && clinicId ? (
          <PrescriptionComposer variant="mobile" petId={pet.id} clinicId={clinicId} appointmentId={medicalRecord.appointment_id || undefined} medicalRecordId={medicalRecord.id} speciesId={pet.species_id ?? undefined} />
        ) : null}
        {activeTab === "vacinas" && clinicId ? (
          <ApplicationTab variant="mobile" petId={pet.id} clinicId={clinicId} appointmentId={medicalRecord.appointment_id || undefined} medicalRecordId={medicalRecord.id} speciesId={pet.species_id ?? undefined} />
        ) : null}
        {activeTab === "termos" && clinicId ? (
          <PatientTermsPanel variant="mobile" petId={pet.id} clinicId={clinicId} medicalRecordId={medicalRecord.id} title={t("termsPanel.title")} description={t("termsPanel.description")} />
        ) : null}
      </MedicalRecordMobileLayout>
      <div className="hidden md:block space-y-6 pb-32">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {t("editTitle")}
            </h1>
            {(responsibleName || veterinarianName) && (
              <p className="mt-1 text-sm text-muted-foreground">
                {[
                  responsibleName ? t("summary.responsibleName", { name: responsibleName }) : null,
                  veterinarianName ? t("summary.veterinarianName", { name: veterinarianName }) : null,
                ].filter(Boolean).join(" • ")}
              </p>
            )}
          </div>
        </div>
      </div>

      <MedicalRecordPatientSummaryCard pet={pet} age={age} lastWeight={lastWeight} />

      <div className="grid gap-4 lg:grid-cols-3 lg:gap-6">
        <div className="min-w-0 space-y-4 order-2 lg:order-none lg:col-span-2 lg:col-start-1 lg:row-start-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">
                {t("clinicalRecordTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SegmentedTabs
                value={activeTab}
                variant="outline"
                onValueChange={(value) => {
                  const nextValue = value as "novo" | "exames" | "prescricao" | "vacinas" | "termos";
                  const nextRouteTab = Object.entries(ROUTE_TAB_TO_LEGACY).find(([, legacy]) => legacy === nextValue)?.[0] ?? "record";
                  navigate(`/medical-records/${id}/edit/${nextRouteTab}`, { replace: true });
                }}
                fill="screen"
                width="full"
                items={[
                  {
                    value: "novo",
                    label: t("tabs.record"),
                    content: (
                      !clinicId ? (
                        <Alert variant="danger">
                          <ShieldAlert className="h-4 w-4" />
                          <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                          <AlertDescription>
                            {t("alerts.recordClinicUndefined")}
                          </AlertDescription>
                        </Alert>
                      ) : !canEdit && editBlockReason === "no_permission" ? (
                        <Alert variant="danger">
                          <ShieldAlert className="h-4 w-4" />
                          <AlertTitle>{t("alerts.accessDeniedTitle")}</AlertTitle>
                          <AlertDescription>
                            {t("noEditPermission")}
                          </AlertDescription>
                        </Alert>
                      ) : !canEdit && editBlockReason === "not_creator" ? (
                        <Alert variant="danger">
                          <ShieldAlert className="h-4 w-4" />
                          <AlertTitle>{t("alerts.editRestrictedTitle")}</AlertTitle>
                          <AlertDescription>
                            {t("alerts.editRestrictedDescription")}
                          </AlertDescription>
                        </Alert>
                      ) : !isMobile ? (
                        <MedicalRecordForm
                          ref={formRef}
                          formController={formController}
                          onCancel={handleBack}
                          isLoading={isSubmitting}
                        />
                      ) : null
                    ),
                  },
                  {
                    value: "exames",
                    label: t("tabs.exams"),
                    content: (
                      !clinicId ? (
                        <Alert variant="danger">
                          <ShieldAlert className="h-4 w-4" />
                          <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                          <AlertDescription>
                            {t("alerts.examsClinicUndefined")}
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <>
                          <MedicalRecordDocumentsSection
                            clinicId={clinicId}
                            attachments={examAttachments}
                            isLoadingAttachments={isLoadingAttachments}
                            onUploadAttachments={uploadAttachments}
                            onDeleteAttachment={deleteAttachment}
                            onUpdateAttachment={updateAttachment}
                          />
                        </>
                      )
                    ),
                  },
                  {
                    value: "prescricao",
                    label: t("tabs.prescriptions"),
                    content: (
                      clinicId ? (
                        <PrescriptionComposer
                          petId={pet.id}
                          clinicId={clinicId}
                          appointmentId={medicalRecord.appointment_id || undefined}
                          medicalRecordId={medicalRecord.id}
                          speciesId={pet.species_id ?? undefined}
                        />
                      ) : (
                        <Alert variant="danger">
                          <ShieldAlert className="h-4 w-4" />
                          <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                          <AlertDescription>
                            {t("alerts.prescriptionsClinicUndefined")}
                          </AlertDescription>
                        </Alert>
                      )
                    ),
                  },
                  {
                    value: "vacinas",
                    label: t("tabs.applications"),
                    content: (
                      clinicId ? (
                        <ApplicationTab
                          petId={pet.id}
                          clinicId={clinicId}
                          appointmentId={medicalRecord.appointment_id || undefined}
                          medicalRecordId={id}
                          speciesId={pet.species_id ?? undefined}
                        />
                      ) : (
                        <Alert variant="danger">
                          <ShieldAlert className="h-4 w-4" />
                          <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                          <AlertDescription>
                            {t("alerts.applicationsClinicUndefined")}
                          </AlertDescription>
                        </Alert>
                      )
                    ),
                  },
                  {
                    value: "termos",
                    label: t("tabs.terms"),
                    content: (
                      clinicId ? (
                        <PatientTermsPanel
                          petId={pet.id}
                          clinicId={clinicId}
                          medicalRecordId={medicalRecord.id}
                          title={t("termsPanel.title")}
                          description={t("termsPanel.description")}
                        />
                      ) : (
                        <Alert variant="danger">
                          <ShieldAlert className="h-4 w-4" />
                          <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                          <AlertDescription>
                            {t("alerts.termsClinicUndefined")}
                          </AlertDescription>
                        </Alert>
                      )
                    ),
                  },
                ]}
              />
            </CardContent>
          </Card>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="shadow-soft-search"
              onClick={handleBack}
            >
              <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              className="w-full sm:w-auto"
              disabled={isSubmitting || !canEdit}
              onClick={() => formRef.current?.submit()}
            >
              <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
              <span className="font-normal">
                {isSubmitting ? t("actions.saving") : t("save", { ns: "common" })}
              </span>
            </Button>
          </div>
        </div>
        <div className="space-y-4 order-1 lg:order-none lg:col-span-1 lg:col-start-3 lg:row-start-1">
          {activeTab === "prescricao" && clinicId ? (
            <PrescriptionDraftPreviewCard className="hidden lg:flex" />
          ) : null}
          <Card className="hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CustomIcons name="paws" className="h-4 w-4 text-current" />
                {t("patientCard.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  {petPhoto ? (
                    <AvatarImage src={petPhoto} alt={pet.name} />
                  ) : (
                    <AvatarFallback className="bg-muted/50 text-muted-foreground">
                      <CustomIcons name="paws" className="h-5 w-5 text-current" />
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/patients/pets/${pet.id}`}
                    className="block truncate text-base font-semibold text-primary hover:underline"
                  >
                    {pet.name}
                  </Link>
                  {petSubtitle ? (
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {petSubtitle}
                    </p>
                  ) : null}
                </div>
              </div>

              {(petAllergies.length > 0 || petTemperaments.length > 0) ? (
                <div className="grid gap-2">
                  {petAllergies.length > 0 ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.allergies")}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {petAllergies.map((item) => {
                          const name = item.condition?.name ?? item.name ?? "";
                          if (!name) return null;
                          return (
                            <Badge key={item.id} variant="default">
                              {name}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {petTemperaments.length > 0 ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.temperament")}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {petTemperaments.map((item) => {
                          const name = item.condition?.name ?? item.name ?? "";
                          if (!name) return null;
                          return (
                            <Badge key={item.id} variant="secondary">
                              {name}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.gender")}</p>
                  <p className="text-sm font-medium text-foreground">{pet.gender?.name ?? t("patientCard.notInformedFemale")}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.registered")}</p>
                  <p className="text-sm font-medium text-foreground">{pet.sterilization?.name ?? t("patientCard.notInformedMale")}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.recentWeight")}</p>
                  <p className="text-sm font-medium text-foreground">
                    {lastWeight?.weight ? `${lastWeight.weight} kg` : t("patientCard.notInformedMale")}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.date")}</p>
                  <p className="text-sm font-medium text-foreground">
                    {lastWeight?.measured_at
                      ? formatDate(lastWeight.measured_at)
                      : "-"}
                  </p>
                </div>
              </div>

              {pet.notes ? (
                <div className="rounded-md border bg-muted/30 p-3">
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.patientNotes")}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{pet.notes}</p>
                </div>
              ) : null}

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold text-foreground">{t("patientCard.guardian")}</p>
                </div>
                {pet.owner?.id && pet.owner?.name ? (
                  <Link
                    to={`/patients/owners/${pet.owner.id}`}
                    className="block text-base font-semibold text-primary hover:underline"
                  >
                    {pet.owner.name}
                  </Link>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("patientCard.noGuardian")}</p>
                )}
                <div className="grid gap-3 sm:grid-cols-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Phone className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.phone")}</p>
                      <p className="truncate text-sm text-foreground">
                        {ownerPrimaryPhoneLabel ? (
                          ownerPrimaryPhone?.is_whatsapp ? (
                            <WhatsAppPhoneLink
                              phoneNumber={ownerPrimaryPhoneLabel}
                              className="min-w-0"
                              textClassName="truncate text-sm text-foreground hover:text-primary"
                            />
                          ) : (
                            ownerPrimaryPhoneLabel
                          )
                        ) : (
                          t("patientCard.noPhone")
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.email")}</p>
                      <TruncatedTextTooltip
                        text={ownerPrimaryEmail ?? t("patientCard.noEmail")}
                        className="truncate text-sm text-foreground"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {veterinarianName ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-4 w-4" />
                  {t("summary.veterinarian")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-foreground">{veterinarianName}</p>
              </CardContent>
            </Card>
          ) : null}

          {/* The prescriptions tab keeps this column for the live preview only. */}
          {activeTab !== "prescricao" ? (
          <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-4 w-4" />
                {t("recentHistory.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {lastMedicalRecord ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-muted-foreground">{t("recentHistory.lastVisit")}</p>
                    <span className="font-medium">
                      {(() => {
                        const formatted = formatDateTimeBR(lastMedicalRecord.consultation_date);
                        return formatted === "-" ? "-" : formatted.replace(" ", " às ");
                      })()}
                    </span>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-muted-foreground">{t("recentHistory.chiefComplaint")}</p>
                    <p className="font-medium">
                      {lastMedicalRecord.chief_complaint}
                    </p>
                    {lastMedicalRecordVetName ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t("recentHistory.veterinarianName", { name: lastMedicalRecordVetName })}
                      </p>
                    ) : null}
                  </div>
                  {lastMedicalRecord.treatment && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-muted-foreground">{t("recentHistory.treatment")}</p>
                        <p className="text-muted-foreground">
                          {lastMedicalRecord.treatment.length > 160
                            ? `${lastMedicalRecord.treatment.slice(0, 160)}...`
                            : lastMedicalRecord.treatment}
                        </p>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">
                  {t("recentHistory.empty")}
                </p>
              )}
            </CardContent>
          </Card>

          <AttendanceCarePanel
            petId={pet.id}
            ownerId={pet.owner?.id ?? null}
            clinicId={clinicId}
          />
          </>
          ) : null}
        </div>
      </div>

      {returnVaccineConflictContext ? (
        <ReturnApplicationConflictDialog
          open={returnVaccineConflictOpen}
          onOpenChange={(open) => {
            if (!open) {
              resolveReturnVaccineConflict(null);
              return;
            }
            setReturnVaccineConflictOpen(true);
          }}
          followUpDate={returnVaccineConflictContext.followUpDate}
          vaccineAppointments={returnVaccineConflictContext.vaccineAppointments}
          value={returnVaccineConflictChoice}
          onValueChange={setReturnVaccineConflictChoice}
          onConfirm={() => resolveReturnVaccineConflict(returnVaccineConflictChoice)}
        />
      ) : null}

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={isExitDialogOpen} onOpenChange={setIsExitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("exitDialog.editTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("exitDialog.editDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelExit}>
              {t("actions.continueEditing")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmExit}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("actions.discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <MedicalRecordExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        medicalRecordId={id!}
      />
      <ProfileDataModal
        open={showProfileDataModal}
        onOpenChange={(open) => {
          setShowProfileDataModal(open);
          if (!open) {
            setPendingMedicalRecordUpdate(null);
          }
        }}
        needsCRMV
        needsMAPA={false}
        needsAddress={false}
        onComplete={handleProfileDataComplete}
        contextLabel="salvar o prontuário"
      />

      {pet ? (
        <MobilePatientEditFlow
          petId={pet.id}
          ownerId={pet.owner?.id}
          open={isMobileEditOpen}
          onOpenChange={setIsMobileEditOpen}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["pet", pet.id] });
          }}
        />
      ) : null}
      </div>
    </PrescriptionDraftProvider>
  );
};

export default ViewMedicalRecord;
